import React, { useEffect, useState } from 'react';

const TimelineNode = ({ event, track, onClick, colors, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // For professional track, show title and subtitle separately
  const isProfessional = track === 'professional';
  const displayTitle = event.title;
  const displaySubtitle = event.subtitle || event.company || event.platform || event.milestone;

  return (
    <div 
      className={`relative transition-all duration-700 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Year badge - always visible on mobile, hidden on xl+ where years column is visible */}
      <div className={`xl:hidden text-xs font-bold tracking-widest mb-1 md:mb-2 ${colors.text} flex items-center gap-1 md:gap-2`}>
        <div className={`w-1 h-4 md:h-5 ${colors.bg} rounded-full`} />
        <span>{event.year}</span>
      </div>

      {/* Node circle with glow effect - smaller on mobile */}
      <div className="relative flex justify-center mb-2 md:mb-4">
        <div className={`absolute inset-0 rounded-full blur-xl transition-all duration-500 ${
          isHovered ? `${colors.bg} opacity-40 scale-150` : 'opacity-0 scale-100'
        }`} />
        
        <button
          onClick={onClick}
          data-testid={`timeline-node-${track}-${event.year}`}
          className={`relative w-8 h-8 md:w-14 md:h-14 rounded-full bg-gradient-to-br ${colors.primary} text-white 
            transition-all duration-500 flex items-center justify-center text-sm md:text-xl group 
            cursor-pointer shadow-md md:shadow-xl ${
            isHovered ? `scale-110 ${colors.glow} shadow-2xl` : 'scale-100'
          }`}
          aria-label={`View details for ${event.title}`}
        >
          <span className={`transition-all duration-300 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}>
            ✦
          </span>
        </button>
      </div>

      {/* Event card - more compact on mobile */}
      <div 
        onClick={onClick}
        className={`relative backdrop-blur-sm bg-white/90 p-2 md:p-4 rounded-lg md:rounded-xl border ${colors.border} ${colors.hover} 
          transition-all duration-300 cursor-pointer group overflow-hidden ${
          isHovered ? `shadow-xl ${colors.glow} -translate-y-1` : 'shadow-md hover:shadow-lg'
        }`}
      >
        {/* Gradient overlay on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colors.primary} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
        
        <div className="relative z-10">
          {/* Title - compact */}
          <h4 className={`text-xs md:text-base font-bold mb-0.5 ${colors.text} leading-tight line-clamp-2`}>
            {displayTitle}
          </h4>
          
          {/* Subtitle for professional track */}
          {isProfessional && event.subtitle && (
            <p className="hidden sm:block text-xs text-gray-600 font-medium line-clamp-1">
              {event.subtitle}
            </p>
          )}
          
          {/* Company / Platform / Milestone - always on its own line */}
          {event.company && (
            <p className="hidden sm:block text-xs text-gray-400 mt-0.5 line-clamp-1">
              {event.company}
            </p>
          )}
          {!event.company && (event.platform || event.milestone) && (
            <p className="hidden sm:block text-xs text-gray-500 font-medium line-clamp-1">
              {event.platform || event.milestone}
            </p>
          )}
          
          {/* Description - hidden on mobile */}
          <p className="hidden md:block text-xs text-gray-500 line-clamp-2 leading-relaxed mt-1">
            {event.description}
          </p>
          
          {/* Explore indicator - smaller on mobile */}
          <div className="mt-1 md:mt-3 flex items-center text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors duration-300">
            <span className="hidden sm:inline">Deep dive</span>
            <span className="sm:hidden">→</span>
            <svg 
              className={`hidden sm:block ml-1 w-3 h-3 md:w-4 md:h-4 transition-all duration-300 ${
                isHovered ? 'translate-x-1' : 'translate-x-0'
              }`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Corner accent - smaller */}
        <div className={`absolute top-0 right-0 w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br ${colors.primary} opacity-10 rounded-bl-full`} />
      </div>
    </div>
  );
};

export default TimelineNode;
